import {
  ApiFile,
  DailyReportDetailSectionDocument,
  DailyReportDetailSectionQuery,
  GetInstallationDeliveriesDocument,
  GetInstallationDeliveriesQuery,
  RemoveImageFromDelivery_Input,
  SaveDelivery_Input,
  SaveDeliveryMutation,
  useGetDeliveryDetailQuery,
  useRemoveDeliveryAttachmentMutation,
  useSaveDeliveryMutation
} from 'generated/graphql';
import _ from 'lodash';
import { DateTime } from 'luxon';
import {
  appFormUtil,
  gqlCacheUtil
} from 'modules/common';
import React, { useEffect } from 'react';

export type IFormState = {
  id: string;
  materialName: string;
  quantity: string;
  description: string;
  connectedToInstallation: boolean;
  installationId: string | null;
  damages: string;
  issues: string | null;
  otherEvents: string;
  receivedDate: string;
  attachments: Partial<ApiFile>[];
};

const initialState: IFormState = {
  id: '',
  materialName: '',
  quantity: '',
  receivedDate: DateTime.now().toFormat('yyyy-LL-dd'),
  description: '',
  installationId: '',
  connectedToInstallation: false,
  damages: '',
  issues: '',
  otherEvents: '',
  attachments: []
};

export function useFormState(id: string, currentInstallationId: string) {
  const {
    formState,
    dispatch,
    genFieldProps
  } = appFormUtil.useFormState({
    initialState,
    key2FieldConfig: {
      id: {
        label: 'Id',
        type: 'text'
      },
      receivedDate: {
        label: 'Date Received',
        type: 'date'
      },
      materialName: {
        label: 'Material Name',
        type: 'text'
      },
      quantity: {
        label: 'Quantity',
        type: 'number'
      },
      description: {
        label: 'Description',
        type: 'text'
      },
      connectedToInstallation: {
        label: 'Connected To Finish',
        type: 'button'
      },
      installationId: {
        label: 'Installation Id',
        type: 'text'
      },
      damages: {
        label: 'Damages',
        type: 'text'
      },
      issues: {
        label: 'Issues',
        type: 'text'
      },
      otherEvents: {
        label: 'Other Events',
        type: 'text'
      },
      attachments: {
        label: 'Files',
        type: 'text'
      }
    }
  });



  useEffect(
    () => {
      if (formState.installationId === '' && currentInstallationId !== '') {
        dispatch({
          type: 'UPDATE',
          payload: {
            property: 'installationId',
            value: currentInstallationId
          }
        });
      }
      return () => {};
    }, [
      currentInstallationId,
      dispatch,
      formState.installationId
    ]
  );

  const { data, loading, called } = useQuery(id);

  useEffect(
    () => {
      if (data.id !== '' && formState.id === '' && !loading && called) {
        dispatch({
          type: 'BATCH_UPDATE',
          payload: {
            ...formState,
            ...data
          }
        });
      }
      return () => {};
    },
    [
      loading,
      called,
      data,
      formState,
      dispatch
    ]
  );

  return React.useMemo(
    () => {
      return {
        formState,
        dispatch,
        genFieldProps
      };
    },
    [
      dispatch,
      formState,
      genFieldProps
    ]
  );
}

export interface IData extends Omit<IFormState, 'installationId'> {}

export function useQuery(id: string) {
  const tuple = useGetDeliveryDetailQuery({
    variables: {
      id
    },
    skip: id === ''
  });

  return React.useMemo(
    () => {
      const delivery = tuple.data?.materialDelivery;
      const data: IData = {
        id: delivery?.id ?? '',
        attachments: delivery?.attachments ?? [],
        connectedToInstallation: true,
        damages: delivery?.damageDescription ?? '',
        description: delivery?.description ?? '',
        issues: delivery?.issueDescription ?? '',
        receivedDate: delivery?.receivedDate?.toFormat('yyyy-LL-dd') ?? '',
        materialName: delivery?.name ?? '',
        otherEvents: delivery?.otherDescription ?? '',
        quantity: `${delivery?.quantity ?? 0}`
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}



export function useSaveDelivery() {
  const [
    fnMutate,
    tuple
  ] = useSaveDeliveryMutation({
    refetchQueries: []
  });

  const fnWrappedMutate = React.useCallback(
    async (input: SaveDelivery_Input) => {
      await fnMutate({
        variables: {
          input
        },
        updateQueries: {
          ...gqlCacheUtil.genUpdateQueryMap<DailyReportDetailSectionQuery, SaveDeliveryMutation>(DailyReportDetailSectionDocument, (prevState, { mutationResult }) => {
            if (!prevState.dailyReport?.id) {
              return prevState;
            }
            if (!mutationResult.data?.saveDelivery?.id) {
              return prevState;
            }

            const isNew = input.id !== mutationResult.data.saveDelivery.id;
            if (isNew) {
              return {
                ...prevState,
                dailyReport: {
                  ...prevState.dailyReport,
                  deliveries: _.compact([
                    ...prevState.dailyReport.deliveries,
                    mutationResult.data.saveDelivery
                  ])
                }
              };
            }

            return {
              ...prevState,
              dailyReport: {
                ...prevState.dailyReport,
                deliveries: _.compact([
                  ...prevState.dailyReport.deliveries.map((node) => {
                    if (node.id === mutationResult.data?.saveDelivery?.id) {
                      return {
                        ...node,
                        ...mutationResult.data.saveDelivery
                      };
                    }
                    return node;
                  })
                ])
              }

            };

          }),
          ...gqlCacheUtil.genUpdateQueryMap<GetInstallationDeliveriesQuery, SaveDeliveryMutation>(GetInstallationDeliveriesDocument, (prevState, { mutationResult }) => {
            if (!prevState.installation?.id) {
              return {
                ...prevState,
                installation: {
                  ...prevState.installation,
                  id: prevState.installation?.id ?? '',
                  associatedDeliveries: []
                }
              };
            }
            if (!prevState.installation?.associatedDeliveries) {
              return {
                ...prevState,
                installation: {
                  ...prevState.installation,
                  id: prevState.installation.id ?? '',
                  associatedDeliveries: []
                }

              };
            }
            if (!mutationResult.data?.saveDelivery) {
              return prevState;
            }


            const isNew = input.id !== mutationResult.data.saveDelivery.id;
            if (isNew) {
              return {
                ...prevState,
                installation: {
                  ...prevState.installation,
                  associatedDeliveries: _.compact([
                    ...prevState.installation.associatedDeliveries,
                    mutationResult.data.saveDelivery
                  ])
                }
              };
            }

            // return prevState;
            return {
              ...prevState,
              installation: {
                ...prevState.installation,
                associatedDeliveries: _.compact([
                  ...prevState.installation.associatedDeliveries.map((node) => {
                    if (node.id === mutationResult.data?.saveDelivery?.id) {
                      return {
                        ...node,
                        ...mutationResult.data.saveDelivery
                      };
                    }
                    return node;
                  })
                ])
              }

            };
          })
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}


export function useRemoveImageFromDelivery() {
  const [
    fnMutate,
    tuple
  ] = useRemoveDeliveryAttachmentMutation({
    refetchQueries: []
  });

  const fnWrappedMutate = React.useCallback(
    async (input: RemoveImageFromDelivery_Input) => {
      await fnMutate({
        variables: {
          input
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}
